/**
 * MyProgrammesPage — student view of only the programmes they've applied/enrolled
 * in. Replaces the sidebar entry that used to send students to the public catalogue.
 *
 * Surfaces the lifecycle status (pending payment → notify payment → application
 * pending → application approved → waiting for start → active) and the right
 * call-to-action for each state.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { GraduationCap, CreditCard, ClipboardText, CheckCircle, Clock, Warning, ArrowRight, Trash } from "@phosphor-icons/react";
import { getPendingEnrollment, clearPendingEnrollment } from "../lib/pendingEnrollment";
import { toast } from "sonner";

const STATE_META = {
  pending_payment:      { label: "Awaiting payment",     color: "bg-amber-500 text-white",   icon: CreditCard,   cta: "Pay Now",                next: (e) => `/pay/${e.programme_id}` },
  pending_verification: { label: "Payment under review", color: "bg-blue-700 text-white",    icon: Clock,        cta: "View status",            next: () => "/payments" },
  payment_verified:     { label: "Fill application",     color: "bg-purple-700 text-white",  icon: ClipboardText, cta: "Apply Now",             next: (e) => `/application/${e.id}` },
  application_submitted:{ label: "Application pending",  color: "bg-blue-700 text-white",    icon: Clock,        cta: "View application",       next: (e) => `/application/${e.id}` },
  application_rejected: { label: "Application rejected", color: "bg-red-700 text-white",     icon: Warning,      cta: "Resubmit application",   next: (e) => `/application/${e.id}` },
  waiting_for_start:    { label: "Starts soon",          color: "bg-blue-700 text-white",    icon: Clock,        cta: "Programme overview",     next: (e) => `/programmes/${e.programme_id}` },
  active:               { label: "Active",               color: "bg-emerald-600 text-white", icon: CheckCircle,  cta: "Open programme",         next: (e) => `/programmes/${e.programme_id}` },
  inactive:             { label: "Not in session",       color: "bg-slate-700 text-white",   icon: Clock,        cta: "Make Enquiry",           next: (e) => `/#contact?programme=${encodeURIComponent(e.programme?.code || "")}` },
};

function CountdownPill({ targetDate }) {
  // Countdown to programme start (Task 4). Updates every minute — accurate
  // enough for a "starts in 3d 12h" display.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const diff = new Date(targetDate).getTime() - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins  = Math.floor((diff % 3_600_000) / 60_000);
  return (
    <p className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-900 px-2 py-1 text-[11px] font-bold uppercase tracking-widest" data-testid="programme-countdown">
      <Clock size={12} weight="bold" />
      Starts in {days > 0 ? `${days}d ` : ""}{hours > 0 || days > 0 ? `${hours}h ` : ""}{mins}m
    </p>
  );
}

function stateOf(e) {
  // If the programme itself is flagged 'inactive' we surface the enquiry CTA
  // regardless of internal application status, per Section 1 (standardised statuses).
  const progStatus = (e.programme?.status || "").toLowerCase();
  if (progStatus === "inactive" || progStatus === "upcoming") return "inactive";
  if (e.status === "pending_payment") return "pending_payment";
  if (e.status === "pending_verification") return "pending_verification";
  if (["paid", "completed", "partial"].includes(e.payment_status)) {
    if (e.application_status === "not_submitted" || !e.application_status) return "payment_verified";
    if (e.application_status === "submitted") return "application_submitted";
    if (e.application_status === "rejected") return "application_rejected";
    if (e.application_status === "approved") {
      const start = e.programme?.start_date ? new Date(e.programme.start_date) : null;
      if (start && start > new Date()) return "waiting_for_start";
      return "active";
    }
  }
  return "pending_payment";
}

export default function MyProgrammesPage() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => api.get("/enrollments/my").then(r => { setEnrollments(r.data || []); setLoading(false); }).catch(() => setLoading(false));

  useEffect(() => {
    (async () => {
      // Claim any pending-enrolment intent persisted by the public Enrol button.
      const pending = getPendingEnrollment();
      if (pending?.id) {
        try {
          const { data } = await api.post("/enrollments/claim-pending", { programme_id: pending.id });
          if (data.created) toast.success(`Added ${pending.code || "programme"} to your dashboard`);
        } catch { /* swallow — listing still works */ }
        clearPendingEnrollment();
      }
      reload();
    })();
  }, []);

  const withdraw = async (e) => {
    if (!window.confirm(`Withdraw from ${e.programme?.title || "this programme"}? This removes it from your dashboard.`)) return;
    try {
      await api.delete(`/enrollments/${e.id}/withdraw`);
      toast.success("Withdrawn");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not withdraw"); }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="my-programmes-page">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-amber-600 font-bold">My Programmes</p>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-950 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>My applications & enrolments</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">Track every programme you&apos;ve applied for. We&apos;ll walk you from payment &rarr; application form &rarr; admin approval &rarr; programme start.</p>
          </div>
          <Link to="/programmes-catalogue">
            <Button variant="outline" className="rounded-none gap-2" data-testid="explore-more-programmes-btn">
              Explore More Programmes <ArrowRight size={14} />
            </Button>
          </Link>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : enrollments.length === 0 ? (
          <Card className="border border-slate-200 rounded-none">
            <CardContent className="p-8 text-center">
              <GraduationCap size={42} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-900">You haven&apos;t applied to any programme yet</p>
              <p className="text-xs text-slate-500 mt-1 mb-4 max-w-md mx-auto">Explore our specialised SBL programmes and pick the one that matches your role.</p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Link to="/">
                  <Button variant="outline" className="rounded-none gap-2" data-testid="explore-landing-btn">
                    Go to landing page
                  </Button>
                </Link>
                <Link to="/programmes-catalogue">
                  <Button className="bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2" data-testid="browse-catalogue-btn">
                    Browse the catalogue <ArrowRight size={14} />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {enrollments.map(e => {
              const st = stateOf(e);
              const meta = STATE_META[st] || STATE_META.pending_payment;
              const Icon = meta.icon;
              const href = meta.next(e);
              return (
                <Card key={e.id} className={`border ${e.suspended ? "border-red-300 bg-red-50/30" : "border-slate-200"} rounded-none shadow-sm`} data-testid={`my-prog-card-${e.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-widest text-slate-400">{e.programme?.code}</p>
                        <h3 className="text-base font-bold text-slate-950 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{e.programme?.title || e.programme_title}</h3>
                      </div>
                      {e.suspended ? (
                        <Badge className="bg-red-700 text-white rounded-none text-[10px] font-bold uppercase whitespace-nowrap" data-testid={`my-prog-state-${e.id}`}>Suspended</Badge>
                      ) : (
                        <Badge className={`${meta.color} rounded-none text-[10px] font-bold uppercase whitespace-nowrap`} data-testid={`my-prog-state-${e.id}`}>{meta.label}</Badge>
                      )}
                    </div>
                    {e.suspended && (
                      <div className="mb-3 border border-red-300 bg-red-50 px-3 py-2 flex items-start gap-2" data-testid={`my-prog-suspended-banner-${e.id}`}>
                        <Warning size={14} weight="bold" className="text-red-700 mt-0.5 shrink-0" />
                        <div className="text-xs text-red-900">
                          <p className="font-bold">Access suspended by admin</p>
                          {e.suspended_reason && <p className="mt-0.5 text-red-800">{e.suspended_reason}</p>}
                          <p className="mt-0.5 text-red-700/80">Please contact the SBL team to resolve.</p>
                        </div>
                      </div>
                    )}
                    <div className="text-xs text-slate-600 space-y-1 mb-4">
                      {e.total_fee > 0 && <p>Paid <strong>${e.amount_paid?.toFixed(2)}</strong> of ${e.total_fee?.toFixed(2)} {e.balance > 0 && <span className="text-amber-700">· balance ${e.balance?.toFixed(2)}</span>}</p>}
                      {e.programme?.start_date && <p>Programme starts <strong>{new Date(e.programme.start_date).toLocaleDateString()}</strong></p>}
                      {st === "waiting_for_start" && e.programme?.start_date && (
                        <CountdownPill targetDate={e.programme.start_date} />
                      )}
                    </div>
                    {e.suspended ? (
                      <Button disabled className="w-full rounded-none gap-2 text-sm bg-slate-200 text-slate-500 cursor-not-allowed" data-testid={`my-prog-suspended-cta-${e.id}`}>
                        <Warning size={14} weight="bold" /> Programme access locked
                      </Button>
                    ) : st === "pending_payment" ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Link to={`/pay/${e.programme_id}`}>
                          <Button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-none gap-2 text-sm font-bold" data-testid={`my-prog-pay-${e.id}`}>
                            <CreditCard size={14} weight="bold" /> Pay Now
                          </Button>
                        </Link>
                        <Button variant="outline" onClick={() => withdraw(e)} className="w-full rounded-none gap-2 text-sm border-red-300 text-red-700 hover:bg-red-50" data-testid={`my-prog-withdraw-${e.id}`}>
                          <Trash size={14} weight="bold" /> Withdraw
                        </Button>
                      </div>
                    ) : st === "active" && e.balance > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        <Link to={`/programmes/${e.programme_id}`}>
                          <Button variant="outline" className="w-full rounded-none gap-1 text-xs" data-testid={`my-prog-explore-${e.id}`}>
                            <GraduationCap size={14} weight="bold" /> Programme
                          </Button>
                        </Link>
                        <Link to={`/courses?programme=${e.programme_id}`}>
                          <Button variant="outline" className="w-full rounded-none gap-1 text-xs border-blue-300 text-blue-800 hover:bg-blue-50" data-testid={`my-prog-go-courses-${e.id}`}>
                            <ArrowRight size={14} weight="bold" /> Go to Courses
                          </Button>
                        </Link>
                        <Link to={`/pay/${e.programme_id}`}>
                          <Button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-none gap-1 text-xs font-bold" data-testid={`my-prog-pay-${e.id}`}>
                            <CreditCard size={14} weight="bold" /> Pay
                          </Button>
                        </Link>
                      </div>
                    ) : st === "active" ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Link to={`/programmes/${e.programme_id}`}>
                          <Button variant="outline" className="w-full rounded-none gap-2 text-sm" data-testid={`my-prog-cta-${e.id}`}>
                            <GraduationCap size={14} weight="bold" /> Programme
                          </Button>
                        </Link>
                        <Link to={`/courses?programme=${e.programme_id}`}>
                          <Button className="w-full bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2 text-sm" data-testid={`my-prog-go-courses-${e.id}`}>
                            <ArrowRight size={14} weight="bold" /> Go to Courses
                          </Button>
                        </Link>
                      </div>
                    ) : st === "application_submitted" ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Link to={href}>
                          <Button variant="outline" className="w-full rounded-none gap-2 text-sm" data-testid={`my-prog-cta-${e.id}`}>
                            <Icon size={14} weight="bold" /> View application
                          </Button>
                        </Link>
                        <Link to={`/programmes/${e.programme_id}`}>
                          <Button className="w-full bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2 text-sm" data-testid={`my-prog-go-overview-${e.id}`}>
                            <GraduationCap size={14} weight="bold" /> Programme overview
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <Link to={href}>
                        <Button className="w-full bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2 text-sm" data-testid={`my-prog-cta-${e.id}`}>
                          <Icon size={14} weight="bold" /> {meta.cta}
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
